/**
 * Audit Log Types - Shared between Admin and Provider portals
 */

export interface AuditLog {
  id: string;
  userId: string;
  user: {
    email: string;
    organization: string | null;
    role: string;
  };
  action: string;
  entityType: string | null;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditLogResponse {
  data: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const ACTION_LABELS: Record<string, string> = {
  "submission.approve": "Submission Approved",
  "submission.reject": "Submission Rejected",
  "submission.view": "Submission Viewed",
  "submission.create": "Submission Created",
  "submission.update": "Submission Updated",
  "submission.submit": "Submission Submitted",
  "submission.withdraw": "Submission Withdrawn",
  "submission.resubmit": "Submission Resubmitted",
  "user.create": "User Created",
  "user.update": "User Updated",
  "user.delete": "User Deleted",
  "user.login": "User Login",
  "user.logout": "User Logout",
  "provider.create": "Provider Created",
  "provider.update": "Provider Updated",
  "provider.delete": "Provider Deleted",
  "provider.profile.update": "Profile Updated",
  "settings.update": "Settings Updated",
  COLLECTION_ITEM_ADDED: "Collection Item Added",
  COLLECTION_ITEM_UPDATED: "Collection Item Updated",
  COLLECTION_UPDATED: "Collection Updated",
  COLLECTION_CREATED: "Collection Created",
  COLLECTION_DELETED: "Collection Deleted",
  TITLE_ADDED: "Title Added",
  TITLE_DELETED: "Title Deleted",
  TITLE_UPDATED: "Title Updated",
  IMAGES_TOGGLED_TO_R2: "Images Toggled to R2",
  IMAGES_TOGGLED_TO_TMDB: "Images Toggled to TMDB",
  IMAGES_CONFIRMED_AS_R2: "Images Confirmed as R2",
  TITLES_BULK_DELETED: "Titles Bulk Deleted",
  TITLES_BULK_ADDED_TO_COLLECTION: "Added to Collection",
  TITLES_BULK_REMOVED_FROM_COLLECTION: "Removed from Collection",
  COLLECTION_ITEMS_REORDERED: "Items Reordered",
};

export function getActionBadgeVariant(action: string): "approved" | "rejected" | "pending" | "secondary" | "outline" {
  const lowerAction = action.toLowerCase();

  // Green for approvals and successful actions
  if (lowerAction.includes("approve") || lowerAction.includes("login")) {
    return "approved";
  }

  // Red for rejections and deletions
  if (lowerAction.includes("reject") || lowerAction.includes("delete")) {
    return "rejected";
  }

  // Yellow for logout and withdrawals (caution)
  if (lowerAction.includes("logout") || lowerAction.includes("withdraw")) {
    return "pending";
  }

  // Blue/gray for updates and views
  if (lowerAction.includes("update") || lowerAction.includes("view")) {
    return "secondary";
  }

  // Outline for creates, additions, submissions, and toggles
  if (
    lowerAction.includes("create") ||
    lowerAction.includes("add") ||
    lowerAction.includes("toggle") ||
    lowerAction.includes("submit") ||
    lowerAction.includes("resubmit")
  ) {
    return "outline";
  }

  // Default fallback - outline for any unmatched actions
  return "outline";
}

export function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
